import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  BuildingOffice2Icon,
  MapPinIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

export interface Structure {
  id: string
  identifierCode: string
  name: string
  type: string
  region?: string
  district?: string
  latitude?: number
  longitude?: number
  active: boolean
}

const TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  hopital: {
    label: 'Hôpital',
    className: 'bg-primary-50 text-primary-700',
  },
  'centre-sante': {
    label: 'Centre de santé',
    className: 'bg-success-50 text-success-700',
  },
  clinique: {
    label: 'Clinique',
    className: 'bg-purple-50 text-purple-700',
  },
  'poste-sante': {
    label: 'Poste de santé',
    className: 'bg-warning-50 text-warning-700',
  },
  prov: {
    label: 'Établissement',
    className: 'bg-primary-50 text-primary-700',
  },
  dept: {
    label: 'Département',
    className: 'bg-neutral-100 text-neutral-700',
  },
}

function getTypeConfig(type: string) {
  return TYPE_CONFIG[type] ?? { label: type, className: 'bg-neutral-100 text-neutral-700' }
}

interface StructureCardProps {
  structure: Structure
  onEdit: (structure: Structure) => void
  onDelete: (structure: Structure) => void
}

export function StructureCard({ structure, onEdit, onDelete }: StructureCardProps) {
  const typeConfig = getTypeConfig(structure.type)

  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-neutral-200 p-4 transition-all duration-200',
        'hover:border-primary-200 hover:shadow-sm cursor-pointer',
        !structure.active && 'opacity-60'
      )}
      onClick={() => onEdit(structure)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit(structure)
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: icon + info */}
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 shrink-0">
            <BuildingOffice2Icon className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-neutral-900 truncate">
              {structure.name}
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Code : {structure.identifierCode}
            </p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md',
                  typeConfig.className
                )}
              >
                {typeConfig.label}
              </span>
              <Badge variant={structure.active ? 'success' : 'error'} size="sm">
                {structure.active ? 'Actif' : 'Inactif'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onEdit(structure)
            }}
            aria-label="Modifier"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(structure)
            }}
            aria-label="Supprimer"
            className="text-error-600 hover:bg-error-50"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Footer: location */}
      {(structure.region || structure.district || structure.latitude != null) && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-neutral-500">
          <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
          {(structure.region || structure.district) && (
            <span>
              {[structure.region, structure.district].filter(Boolean).join(' / ')}
            </span>
          )}
          {structure.latitude != null && structure.longitude != null && (
            <span className="text-neutral-400 ml-2">
              ({structure.latitude.toFixed(4)}, {structure.longitude.toFixed(4)})
            </span>
          )}
        </div>
      )}
    </div>
  )
}
